import algoliasearch from 'algoliasearch';
import { get, head, map, isEmpty, sortBy, toNumber, reduce } from 'lodash';

const FAUX_ALPHA_QUERY = 'a';
const MAX_ALGOLIA_HITS = 100;

// configure algolia
const client = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_SEARCH_ONLY_KEY
);

const actorIndex = client.initIndex(process.env.REACT_APP_ALGOLIA_ACTOR_INDEX);
const showIndex = client.initIndex(process.env.REACT_APP_ALGOLIA_SHOW_INDEX);
const movieIndex = client.initIndex(process.env.REACT_APP_ALGOLIA_MOVIE_INDEX);
const networkIndex = client.initIndex(
  process.env.REACT_APP_ALGOLIA_NETWORK_INDEX
);
const platformIndex = client.initIndex(
  process.env.REACT_APP_ALGOLIA_PLATFORM_INDEX
);
const studioIndex = client.initIndex(
  process.env.REACT_APP_ALGOLIA_STUDIO_INDEX
);
const networkOptionFormatter = network => ({
  value: network.company_id,
  label: network.name
});

const platformOptionFormatter = platform => {
  return {
    value: platform.platform_name,
    label: platform.platform_name
  };
};

/* Helper function to retrieve only unique options based
/**
 *
 * @param {algolia index} index algolia index where the options will be looked for
 * @param {string}        query search string
 *
 */

const getFilterOptions = async (
  index,
  {
    query = '',
    facetFilters = [],
    filters = '',
    optionFormatter = option => ({
      value: option.id || option.MOVIE_TVT_UUID,
      label: option.name || option.MOVIE_NAME
    }),
    sort = true,
    contentType = false,
    hitsPerPage = MAX_ALGOLIA_HITS
  }
) => {
  try {
    const results = await index.search({
      query,
      hitsPerPage,
      facetFilters,
      filters
    });

    const options = map(
      sort
        ? sortBy(results.hits, hit =>
            /\(US\)/.test(hit.name)
              ? Number.NEGATIVE_INFINITY
              : -get(hit, 'followers', 0)
          )
        : results.hits,
      optionFormatter
    );

    const final = { options };
    if (contentType) {
      final.contentType = contentType;
    }

    return final;
  } catch (error) {
    return Promise.reject(error);
  }
};

const getPlatformFilterOptionsFromAlgolia = async (
  index,
  {
    query = '',
    facetFilters = [],
    platforms = [],
    excludedPlatforms = [],
    optionFormatter = option => ({
      value: option.id || option.MOVIE_TVT_UUID,
      label: option.name || option.MOVIE_NAME
    }),
    contentType = false,
    hitsPerPage = 100000
  }
) => {
  try {
    const results = await index.search({
      query,
      hitsPerPage,
      facetFilters
    });
    const options = results.hits
      .filter(
        hit =>
          platforms.includes(hit.platform_name) &&
          !excludedPlatforms.includes(hit.platform_name)
      )
      .map(optionFormatter);

    const final = { options };
    if (contentType) {
      final.contentType = contentType;
    }

    return final;
  } catch (error) {
    return Promise.reject(error);
  }
};

const addYearToDuplicateHits = hits => {
  const hitsSet = {};
  hits.forEach(hit => {
    if (hitsSet[hit.MOVIE_NAME]) {
      hitsSet[hit.MOVIE_NAME] += 1;
    } else {
      hitsSet[hit.MOVIE_NAME] = 1;
    }
  });

  return hits.map(hit => {
    if (hitsSet[hit.MOVIE_NAME] > 1) {
      return { ...hit, MOVIE_NAME: hit.SEARCH_RESULT };
    }

    return hit;
  });
};

const getMovieFilterOptions = async (
  index,
  {
    query = '',
    facetFilters = [],
    filters = '',
    optionFormatter = option => ({
      value: option.MOVIE_TVT_UUID,
      label: option.MOVIE_NAME
    }),
    sort = true,
    contentType = false,
    hitsPerPage = MAX_ALGOLIA_HITS
  }
) => {
  try {
    const results = await index.search({
      query,
      hitsPerPage,
      facetFilters,
      filters
    });

    const hits = addYearToDuplicateHits(results.hits);
    const options = map(
      sort
        ? sortBy(hits, hit =>
            /\(US\)/.test(hit.name)
              ? Number.NEGATIVE_INFINITY
              : -get(hit, 'followers', 0)
          )
        : hits,
      optionFormatter
    );

    const final = { options };
    if (contentType) {
      final.contentType = contentType;
    }

    return final;
  } catch (error) {
    return Promise.reject(error);
  }
};

const checkQuery = query => (isEmpty(query) ? FAUX_ALPHA_QUERY : query);

const algoliaAPI = {
  /**
   *
   * @param {string} query search string
   *
   */
  getActorFilterOptions: query =>
    getFilterOptions(actorIndex, {
      query,
      optionFormatter: opt => ({ ...opt, id: opt.tvdb_id })
    }),

  /**
   *
   * @param {string} query search string
   *
   */

  getMovieFilterOptions: query =>
    getMovieFilterOptions(movieIndex, {
      query: checkQuery(query),
      sort: false
    }),

  getShowFilterOptions: query =>
    getFilterOptions(showIndex, { query: checkQuery(query), sort: false }),

  getShowsById: async ids => {
    const newShowIds = [];

    ids.forEach(e => {
      if (e.includes('not')) newShowIds.push(e.replace('not ', ''));
      else newShowIds.push(e);
    });

    const newFilter = map(newShowIds, id => `id = ${id}`).join(' OR ');
    const getFilter = await getFilterOptions(showIndex, {
      filters: newFilter
    });

    const sendModifiedParamsToState = [];

    for (let i = 0; i < getFilter?.options?.length; i += 1) {
      let modifiedLabel = getFilter?.options[i]?.label;

      if (ids?.length && ids[i]?.includes('not')) {
        modifiedLabel = `Excludes: ${modifiedLabel}`;
      }
      sendModifiedParamsToState.push({
        value: ids[i],
        label: `${modifiedLabel}`
      });
    }

    getFilter.options = sendModifiedParamsToState;
    return getFilter;
  },

  getBulkMoviesByName: async movieNames =>
    movieNames.reduce(async (acc, query) => {
      const res = await acc;
      const opts = await getFilterOptions(movieIndex, {
        query: checkQuery(query),
        sort: false
      });
      const result = get(opts, ['options', 0], false);
      if (result) {
        res.push(result);
      }
      return res;
    }, Promise.resolve([])),

  getBulkShowsByName: async showNames =>
    showNames.reduce(async (acc, query) => {
      const res = await acc;
      const opts = await getFilterOptions(showIndex, {
        query: checkQuery(query),
        sort: false
      });
      const result = get(opts, ['options', 0], false);
      if (result) {
        res.push(result);
      }
      return res;
    }, Promise.resolve([])),

  getTitlesById: ids => {
    const titleIds = reduce(
      ids,
      (memo, id) => {
        // if id is number hit shows index, if not than movies
        if (!isNaN(toNumber(id))) {
          if (!memo.shows) {
            memo.shows = [];
          }
          memo.shows.push(id);
        } else {
          if (!memo.movies) {
            memo.movies = [];
          }
          memo.movies.push(id);
        }
        return memo;
      },
      {}
    );

    const promises = [];

    if (titleIds.shows) {
      promises.push(
        getFilterOptions(showIndex, {
          filters: map(titleIds.shows, id => `id = ${id}`).join(' OR ')
        })
      );
    }

    if (titleIds.movies) {
      promises.push(
        getFilterOptions(movieIndex, {
          filters: map(titleIds.movies, id => `MOVIE_TVT_UUID:"${id}"`).join(
            ' OR '
          )
        })
      );
    }

    return Promise.all(promises);
  },

  getMoviesById: async ids => {
    const newMovieIds = [];

    ids.forEach(e => {
      if (e.startsWith('-')) newMovieIds.push(e.substring(1));
      else newMovieIds.push(e);
    });

    const getFilter = await getFilterOptions(movieIndex, {
      filters: map(newMovieIds, id => `MOVIE_TVT_UUID:"${id}"`).join(' OR ')
    });

    const sendModifiedMovieParams = [];

    for (let i = 0; i < getFilter?.options?.length; i += 1) {
      let modifiedLabel = getFilter?.options[i]?.label;

      if (ids?.length && ids[i]?.startsWith('-')) {
        modifiedLabel = `Excludes: ${modifiedLabel}`;
      }

      sendModifiedMovieParams.push({
        value: ids[i],
        label: `${modifiedLabel}`
      });
    }

    getFilter.options = sendModifiedMovieParams;
    return getFilter;
  },

  getTitleFilterOptions: async (query, restrictedCategories = []) => {
    const promises = [];

    if (!restrictedCategories.includes('show')) {
      promises.push(
        getFilterOptions(showIndex, {
          query: checkQuery(query),
          sort: false,
          contentType: 'shows',
          hitsPerPage: 8
        })
      );
    }

    if (!restrictedCategories.includes('movie')) {
      promises.push(
        getMovieFilterOptions(movieIndex, {
          query: checkQuery(query),
          sort: false,
          contentType: 'movies',
          hitsPerPage: 8
        })
      );
    }
    const opts = await Promise.all(promises);
    return opts;
  },
  /**
   *
   * @param {string} query search string
   *
   */
  getStudioFilterOptions: query =>
    getFilterOptions(studioIndex, {
      query,
      optionFormatter: studio => ({
        value: studio.STUDIO_ID,
        label: studio.STUDIO_NAME
      })
    }),

  getStudiosById: async ids => {
    const newIds = [];
    ids.forEach(e => {
      if (e.includes('not')) newIds.push(e.replace('not ', ''));
      else newIds.push(e);
    });

    const newFilter = map(newIds, id => `STUDIO_ID = ${id}`).join(' OR ');

    const getFilter = await getFilterOptions(studioIndex, {
      filters: newFilter,
      optionFormatter: studio => ({
        value: studio.STUDIO_ID,
        label: studio.STUDIO_NAME
      })
    });

    const sendModifiedParamsToState = [];
    for (let i = 0; i < getFilter.options.length; i += 1) {
      let modifiedLabel = getFilter.options[i].label;
      const modifiedBase = ids[i];

      if (modifiedBase && modifiedBase.includes('not')) {
        modifiedLabel = `Excludes: ${modifiedLabel}`;
      }
      sendModifiedParamsToState.push({
        value: ids[i],
        label: `${modifiedLabel}`,
        base: modifiedBase
      });
    }
    getFilter.options = sendModifiedParamsToState;
    return getFilter;
  },

  getPlatformFilterOptions: query =>
    getFilterOptions(platformIndex, {
      query,
      optionFormatter: platformOptionFormatter
    }),

  getPlatformsById: async ids => {
    const platforms = [];
    const excludedPlatforms = [];

    ids.forEach(e => {
      if (e.includes('not')) {
        excludedPlatforms.push(e.replace('not ', ''));
      } else {
        platforms.push(e);
      }
    });

    const getFilter = await getPlatformFilterOptionsFromAlgolia(platformIndex, {
      platforms,
      optionFormatter: platformOptionFormatter
    });

    const sendModifiedParamsToState = [];

    for (let i = 0; i < getFilter.options.length; i += 1) {
      let modifiedLabel = getFilter.options[i].label;
      const modifiedBase = ids[i];

      if (modifiedBase && modifiedBase.includes('not')) {
        modifiedLabel = `Excludes: ${modifiedLabel}`;
      }
      sendModifiedParamsToState.push({
        value: ids[i],
        label: `${modifiedLabel}`,
        base: modifiedBase
      });
    }
    getFilter.options = sendModifiedParamsToState;
    return getFilter;
  },

  getNetworkFilterOptions: query =>
    getFilterOptions(networkIndex, {
      query: checkQuery(query),
      optionFormatter: networkOptionFormatter
    }),

  getNetworksById: async ids => {
    const newIds = [];
    ids.forEach(e => {
      if (e.includes('not')) newIds.push(e.replace('not ', ''));
      else newIds.push(e);
    });

    const newFilter = map(newIds, id => `company_id = ${id}`).join(' OR ');

    const getFilter = await getFilterOptions(networkIndex, {
      filters: newFilter,
      optionFormatter: networkOptionFormatter
    });

    const sendModifiedParamsToState = [];
    for (let i = 0; i < getFilter.options.length; i += 1) {
      let modifiedLabel = getFilter.options[i].label;
      const modifiedBase = ids[i];

      if (modifiedBase && modifiedBase.includes('not')) {
        modifiedLabel = `Excludes: ${modifiedLabel}`;
      }
      sendModifiedParamsToState.push({
        value: ids[i],
        label: `${modifiedLabel}`,
        base: modifiedBase
      });
    }
    getFilter.options = sendModifiedParamsToState;
    return getFilter;
  },

  /**
   *
   * @param {string} networkId // objectId defined by Algolia
   *
   */
  getNetworkDataTVTimeId: async networkId => {
    try {
      const response = await networkIndex.search({
        filters: `company_id = ${networkId}`
      });
      return response.hits.length < 1
        ? Promise.reject(
            new Error(`No networks with id ${networkId} found in index`)
          )
        : Promise.resolve(head(response.hits));
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // default
  getNetworkData: networkId => algoliaAPI.getNetworkDataTVTimeId(networkId),

  getSearchPageResults: (query, allowedPages) => {
    const promises = [];
    if (allowedPages.includes('/shows')) {
      promises.push(
        getFilterOptions(showIndex, {
          query: checkQuery(query),
          sort: false,
          hitsPerPage: 10,
          optionFormatter: opt => ({
            title: opt.name,
            image: opt.image,
            link: `/show/${opt.id}`,
            description: opt.network
          })
        })
      );
    }
    if (allowedPages.includes('/movies')) {
      promises.push(
        getFilterOptions(movieIndex, {
          query: checkQuery(query),
          sort: false,
          hitsPerPage: 10,
          optionFormatter: opt => ({
            title: opt.SEARCH_RESULT,
            image: opt.image,
            link: `/movie/${opt.MOVIE_TVT_UUID}`
          })
        })
      );
    }
    if (allowedPages.includes('/networks')) {
      promises.push(
        getFilterOptions(networkIndex, {
          query: checkQuery(query),
          sort: false,
          hitsPerPage: 10,
          optionFormatter: opt => ({
            title: opt.name,
            image: opt.image,
            link: `/network/${opt.id}`
          })
        })
      );
    }
    if (allowedPages.includes('/actors')) {
      promises.push(
        getFilterOptions(actorIndex, {
          query: checkQuery(query),
          sort: false,
          hitsPerPage: 10,
          optionFormatter: opt => ({
            title: opt.name,
            image: get(opt, ['poster', '1'], null),
            link: `/actor/${opt.tvdb_id}`
          })
        })
      );
    }
    return Promise.all(promises);
  }
};

export default algoliaAPI;
